import axios from 'axios';
import { tokenStore } from './tokenStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = tokenStore.getToken();
  const walkAsUser = tokenStore.getWalkAsUser();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  
  if (walkAsUser) {
    headers['X-Walk-As-User-Id'] = walkAsUser.id;
    headers['X-Walk-As-User-Role'] = walkAsUser.role;
  }
  
  return headers;
};

export const api = {
  // Auth - Enhanced with security features
  login: (email, password, otp = null) => {
    const data = { email, password };
    if (otp) data.otp = otp;
    return axios.post(`${API_URL}/api/auth/login`, data);
  },
  register: (userData) => axios.post(`${API_URL}/api/auth/register`, userData),
  verifyEmail: (token) => axios.get(`${API_URL}/api/auth/verify-email/${token}`),
  resendVerification: (email) => axios.post(`${API_URL}/api/auth/resend-verification`, null, { params: { email } }),
  resendOtp: (email) => axios.post(`${API_URL}/api/auth/resend-otp`, null, { params: { email } }),
  activateAccount: (token, password) => axios.post(`${API_URL}/api/auth/activate-account`, { token, password }),
  changePassword: (currentPassword, newPassword) => 
    axios.post(`${API_URL}/api/auth/change-password`, 
      { current_password: currentPassword, new_password: newPassword }, 
      { headers: getAuthHeader() }
    ),
  // Password Reset (Forgot Password)
  forgotPassword: (email) => axios.post(`${API_URL}/api/auth/forgot-password`, { email }),
  validateResetToken: (token) => axios.get(`${API_URL}/api/auth/reset-password/${token}`),
  resetPassword: (token, newPassword) => axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: newPassword }),
  
  getMe: () => axios.get(`${API_URL}/api/auth/me`, { headers: getAuthHeader() }),
  // Google SSO
  googleSSOLogin: (sessionId) => axios.post(`${API_URL}/api/auth/google-sso`, { session_id: sessionId }),

  // Incidents
  getIncidents: () => axios.get(`${API_URL}/api/incidents`, { headers: getAuthHeader() }),
  getMyCreatedIncidents: (status = null, priority = null) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    const query = params.toString() ? `?${params.toString()}` : '';
    return axios.get(`${API_URL}/api/incidents/my/created${query}`, { headers: getAuthHeader() });
  },
  getIncident: (id) => axios.get(`${API_URL}/api/incidents/${id}`, { headers: getAuthHeader() }),
  getIncidentTimeline: (id) => axios.get(`${API_URL}/api/incidents/${id}/timeline`, { headers: getAuthHeader() }),
  createIncident: (data) => axios.post(`${API_URL}/api/incidents`, data, { headers: getAuthHeader() }),
  updateIncident: (id, data, activityLogEntry = null) => {
    const params = activityLogEntry ? `?activity_log_entry=${encodeURIComponent(activityLogEntry)}` : '';
    return axios.put(`${API_URL}/api/incidents/${id}${params}`, data, { headers: getAuthHeader() });
  },
  
  // Incident Related Items Search
  searchRelatedKnownErrors: (q = '') => 
    axios.get(`${API_URL}/api/incidents/related-items/known-errors`, { headers: getAuthHeader(), params: { q } }),
  searchRelatedIncidents: (q = '', excludeId = null) => 
    axios.get(`${API_URL}/api/incidents/related-items/incidents`, { headers: getAuthHeader(), params: { q, exclude_id: excludeId } }),
  searchRelatedChanges: (q = '') => 
    axios.get(`${API_URL}/api/incidents/related-items/changes`, { headers: getAuthHeader(), params: { q } }),
  searchRelatedServiceRequests: (q = '') => 
    axios.get(`${API_URL}/api/incidents/related-items/service-requests`, { headers: getAuthHeader(), params: { q } }),

  // Change Timeline
  getChangeTimeline: (id) => axios.get(`${API_URL}/api/change-requests/${id}/timeline`, { headers: getAuthHeader() }),
  
  // Problem Timeline
  getProblemTimeline: (id) => axios.get(`${API_URL}/api/problems/${id}/timeline`, { headers: getAuthHeader() }),
  
  // Service Request Timeline
  getServiceRequestTimeline: (id) => axios.get(`${API_URL}/api/service-requests/${id}/timeline`, { headers: getAuthHeader() }),

  // Attachments
  uploadAttachments: (ticketType, ticketId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return axios.post(`${API_URL}/api/attachments/upload/${ticketType}/${ticketId}`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getAttachments: (ticketType, ticketId) => 
    axios.get(`${API_URL}/api/attachments/${ticketType}/${ticketId}`, { headers: getAuthHeader() }),
  downloadAttachment: (attachmentId) => 
    axios.get(`${API_URL}/api/attachments/${attachmentId}/download`, { 
      headers: getAuthHeader(),
      responseType: 'blob'
    }),
  getRequests: () => axios.get(`${API_URL}/api/requests`, { headers: getAuthHeader() }),
  getRequest: (id) => axios.get(`${API_URL}/api/requests/${id}`, { headers: getAuthHeader() }),
  createRequest: (data) => axios.post(`${API_URL}/api/requests`, data, { headers: getAuthHeader() }),
  updateRequest: (id, data) => axios.put(`${API_URL}/api/requests/${id}`, data, { headers: getAuthHeader() }),

  // Changes (Legacy - kept for backward compatibility)
  getChanges: () => axios.get(`${API_URL}/api/changes`, { headers: getAuthHeader() }),
  createChange: (data) => axios.post(`${API_URL}/api/changes`, data, { headers: getAuthHeader() }),
  updateChange: (id, data) => axios.put(`${API_URL}/api/changes/${id}`, data, { headers: getAuthHeader() }),

  // Change Management (New comprehensive module)
  // Change Requests
  getChangeRequests: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.change_type) queryParams.append('change_type', params.change_type);
    if (params.my_changes) queryParams.append('my_changes', params.my_changes);
    if (params.pending_approval) queryParams.append('pending_approval', params.pending_approval);
    return axios.get(`${API_URL}/api/change-requests?${queryParams.toString()}`, { headers: getAuthHeader() });
  },
  getChangeRequest: (id) => axios.get(`${API_URL}/api/change-requests/${id}`, { headers: getAuthHeader() }),
  createChangeRequest: (data) => axios.post(`${API_URL}/api/change-requests`, data, { headers: getAuthHeader() }),
  updateChangeRequest: (id, data) => axios.put(`${API_URL}/api/change-requests/${id}`, data, { headers: getAuthHeader() }),
  submitChangeRequest: (id) => axios.post(`${API_URL}/api/change-requests/${id}/submit`, {}, { headers: getAuthHeader() }),
  approveChangeRequest: (id, decision, comments = null) => 
    axios.post(`${API_URL}/api/change-requests/${id}/approve?decision=${decision}${comments ? `&comments=${encodeURIComponent(comments)}` : ''}`, {}, { headers: getAuthHeader() }),
  startChangeImplementation: (id) => axios.post(`${API_URL}/api/change-requests/${id}/start-implementation`, {}, { headers: getAuthHeader() }),
  completeChangeImplementation: (id, success = true, notes = null) => 
    axios.post(`${API_URL}/api/change-requests/${id}/complete-implementation?success=${success}${notes ? `&implementation_notes=${encodeURIComponent(notes)}` : ''}`, {}, { headers: getAuthHeader() }),
  
  // Change Templates
  getChangeTemplates: (activeOnly = true) => axios.get(`${API_URL}/api/change-templates?active_only=${activeOnly}`, { headers: getAuthHeader() }),
  getChangeTemplate: (id) => axios.get(`${API_URL}/api/change-templates/${id}`, { headers: getAuthHeader() }),
  createChangeTemplate: (data) => axios.post(`${API_URL}/api/change-templates`, data, { headers: getAuthHeader() }),
  updateChangeTemplate: (id, data) => axios.put(`${API_URL}/api/change-templates/${id}`, data, { headers: getAuthHeader() }),
  deleteChangeTemplate: (id) => axios.delete(`${API_URL}/api/change-templates/${id}`, { headers: getAuthHeader() }),
  
  // Change Template Versioning
  getChangeTemplateVersions: (id) => axios.get(`${API_URL}/api/change-templates/${id}/versions`, { headers: getAuthHeader() }),
  getChangeTemplateVersion: (id, version) => axios.get(`${API_URL}/api/change-templates/${id}/versions/${version}`, { headers: getAuthHeader() }),
  restoreChangeTemplateVersion: (id, version) => axios.post(`${API_URL}/api/change-templates/${id}/restore/${version}`, {}, { headers: getAuthHeader() }),
  
  // Workflow Patterns Library
  getWorkflowPatterns: () => axios.get(`${API_URL}/api/workflow-patterns`, { headers: getAuthHeader() }),
  
  // Change Tasks
  getChangeTasks: (changeId) => axios.get(`${API_URL}/api/change-requests/${changeId}/tasks`, { headers: getAuthHeader() }),
  createChangeTask: (changeId, taskData) => {
    const params = new URLSearchParams();
    params.append('task_name', taskData.task_name);
    params.append('task_type', taskData.task_type || 'execution');
    if (taskData.task_description) params.append('task_description', taskData.task_description);
    if (taskData.assignment_group_id) params.append('assignment_group_id', taskData.assignment_group_id);
    if (taskData.assignment_group_name) params.append('assignment_group_name', taskData.assignment_group_name);
    if (taskData.approver_id) params.append('approver_id', taskData.approver_id);
    if (taskData.approver_name) params.append('approver_name', taskData.approver_name);
    if (taskData.sla_hours) params.append('sla_hours', taskData.sla_hours);
    if (taskData.notify_roles) taskData.notify_roles.forEach(role => params.append('notify_roles', role));
    if (taskData.notification_template) params.append('notification_template', taskData.notification_template);
    if (taskData.execution_mode) params.append('execution_mode', taskData.execution_mode);
    if (taskData.depends_on && taskData.depends_on.length > 0) {
      taskData.depends_on.forEach(dep => params.append('depends_on', dep));
    }
    return axios.post(`${API_URL}/api/change-requests/${changeId}/tasks?${params.toString()}`, {}, { headers: getAuthHeader() });
  },
  updateChangeTask: (changeId, taskId, taskData) => {
    return axios.put(`${API_URL}/api/change-requests/${changeId}/tasks/${taskId}`, taskData, { headers: getAuthHeader() });
  },
  reorderChangeTasks: (changeId, newOrder) => {
    return axios.put(`${API_URL}/api/change-requests/${changeId}/tasks/reorder`, { tasks: newOrder }, { headers: getAuthHeader() });
  },
  
  // PIR (Post Implementation Review)
  submitPIR: (changeId, data) => axios.post(`${API_URL}/api/change-requests/${changeId}/submit-pir`, data, { headers: getAuthHeader() }),
  getPIRReviews: (pendingOnly = false) => axios.get(`${API_URL}/api/pir-reviews?pending_only=${pendingOnly}`, { headers: getAuthHeader() }),
  cabReviewPIR: (pirId, approved, comments = null) => 
    axios.post(`${API_URL}/api/pir-reviews/${pirId}/cab-review?approved=${approved}${comments ? `&cab_comments=${encodeURIComponent(comments)}` : ''}`, {}, { headers: getAuthHeader() }),
  
  // Blackout Periods
  getBlackoutPeriods: (activeOnly = true) => axios.get(`${API_URL}/api/blackout-periods?active_only=${activeOnly}`, { headers: getAuthHeader() }),
  createBlackoutPeriod: (data) => {
    const params = new URLSearchParams(data);
    return axios.post(`${API_URL}/api/blackout-periods?${params.toString()}`, {}, { headers: getAuthHeader() });
  },
  deleteBlackoutPeriod: (id) => axios.delete(`${API_URL}/api/blackout-periods/${id}`, { headers: getAuthHeader() }),
  
  // Change Calendar
  getChangeCalendar: (startDate, endDate) => 
    axios.get(`${API_URL}/api/change-calendar?start_date=${startDate}&end_date=${endDate}`, { headers: getAuthHeader() }),
  
  // CAB Settings & Console
  getCABSettings: () => axios.get(`${API_URL}/api/cab-settings`, { headers: getAuthHeader() }),
  updateCABSettings: (data) => axios.put(`${API_URL}/api/cab-settings`, data, { headers: getAuthHeader() }),
  getCABPendingItems: () => axios.get(`${API_URL}/api/cab-console/pending`, { headers: getAuthHeader() }),
  
  // Change Statistics
  getChangeStats: () => axios.get(`${API_URL}/api/change-stats`, { headers: getAuthHeader() }),

  // Problems
  getProblems: () => axios.get(`${API_URL}/api/problems`, { headers: getAuthHeader() }),
  getProblem: (id) => axios.get(`${API_URL}/api/problems/${id}`, { headers: getAuthHeader() }),
  createProblem: (data) => axios.post(`${API_URL}/api/problems`, data, { headers: getAuthHeader() }),
  updateProblem: (id, data) => axios.put(`${API_URL}/api/problems/${id}`, data, { headers: getAuthHeader() }),
  updateProblemStatus: (id, statusData) => axios.patch(`${API_URL}/api/problems/${id}/status`, statusData, { headers: getAuthHeader() }),
  updateProblemRCA: (id, rcaData) => axios.put(`${API_URL}/api/problems/${id}/rca`, rcaData, { headers: getAuthHeader() }),
  linkIncidentsToProblem: (problemId, incidentIds) => axios.post(`${API_URL}/api/problems/${problemId}/link-incidents`, { incident_ids: incidentIds }, { headers: getAuthHeader() }),
  getLinkedIncidents: (problemId) => axios.get(`${API_URL}/api/problems/${problemId}/linked-incidents`, { headers: getAuthHeader() }),
  suggestIncidentsForProblem: (problemId) => axios.get(`${API_URL}/api/problems/${problemId}/suggest-incidents`, { headers: getAuthHeader() }),
  publishProblemToKEDB: (problemId) => axios.post(`${API_URL}/api/problems/${problemId}/publish-to-kedb`, {}, { headers: getAuthHeader() }),
  createChangeFromProblem: (problemId, changeData) => axios.post(`${API_URL}/api/problems/${problemId}/create-change`, changeData, { headers: getAuthHeader() }),
  getProblemDashboardStats: () => axios.get(`${API_URL}/api/problems/dashboard/stats`, { headers: getAuthHeader() }),
  getProblemAlertConfig: () => axios.get(`${API_URL}/api/problems/alerts/config`, { headers: getAuthHeader() }),
  updateProblemAlertConfig: (config) => axios.put(`${API_URL}/api/problems/alerts/config`, config, { headers: getAuthHeader() }),
  testProblemAlerts: () => axios.post(`${API_URL}/api/problems/alerts/test`, {}, { headers: getAuthHeader() }),
  
  // Problem Workflow Management
  getProblemWorkflow: (id) => axios.get(`${API_URL}/api/problems/${id}/workflow`, { headers: getAuthHeader() }),
  submitProblemForReview: (id, data) => axios.post(`${API_URL}/api/problems/${id}/submit-for-review`, data, { headers: getAuthHeader() }),
  returnProblemForRevision: (id, data) => axios.post(`${API_URL}/api/problems/${id}/return-for-revision`, data, { headers: getAuthHeader() }),
  forwardProblem: (id, data) => axios.post(`${API_URL}/api/problems/${id}/forward`, data, { headers: getAuthHeader() }),
  confirmKnownError: (id, data) => axios.post(`${API_URL}/api/problems/${id}/confirm-known-error`, data, { headers: getAuthHeader() }),
  
  // Investigation Requests (Ad-hoc SR for Problem Investigation)
  createInvestigationRequest: (problemId, data) => axios.post(`${API_URL}/api/problems/${problemId}/investigation-requests`, data, { headers: getAuthHeader() }),
  getInvestigationRequests: (problemId) => axios.get(`${API_URL}/api/problems/${problemId}/investigation-requests`, { headers: getAuthHeader() }),
  approveInvestigationRequest: (srId, data) => axios.post(`${API_URL}/api/investigation-requests/${srId}/approve`, data, { headers: getAuthHeader() }),
  respondToInvestigationRequest: (srId, data) => axios.post(`${API_URL}/api/investigation-requests/${srId}/respond`, data, { headers: getAuthHeader() }),

  // Problem Investigation Templates
  getProblemTemplates: () => axios.get(`${API_URL}/api/problem-templates`, { headers: getAuthHeader() }),
  getProblemTemplate: (id) => axios.get(`${API_URL}/api/problem-templates/${id}`, { headers: getAuthHeader() }),
  suggestProblemTemplate: (problemId) => axios.get(`${API_URL}/api/problem-templates/suggest/${problemId}`, { headers: getAuthHeader() }),
  applyProblemTemplate: (problemId, templateId) => axios.post(`${API_URL}/api/problems/${problemId}/apply-template/${templateId}`, {}, { headers: getAuthHeader() }),
  getProblemTasks: (problemId) => axios.get(`${API_URL}/api/problems/${problemId}/tasks`, { headers: getAuthHeader() }),

  // Knowledge Base (legacy)
  getArticles: (search = '') => axios.get(`${API_URL}/api/knowledge${search ? `?search=${search}` : ''}`, { headers: getAuthHeader() }),
  getArticle: (id) => axios.get(`${API_URL}/api/knowledge/${id}`, { headers: getAuthHeader() }),
  createArticle: (data) => axios.post(`${API_URL}/api/knowledge`, data, { headers: getAuthHeader() }),
  updateArticle: (id, data) => axios.put(`${API_URL}/api/knowledge/${id}`, data, { headers: getAuthHeader() }),
  deleteArticle: (id) => axios.delete(`${API_URL}/api/knowledge/${id}`, { headers: getAuthHeader() }),

  // Knowledge Management (new)
  getKnowledgeArticles: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.category) queryParams.append('category', params.category);
    if (params.search) queryParams.append('search', params.search);
    if (params.assignment_group_id) queryParams.append('assignment_group_id', params.assignment_group_id);
    if (params.visible_to_end_users !== undefined) queryParams.append('visible_to_end_users', params.visible_to_end_users);
    if (params.created_by_me) queryParams.append('created_by_me', params.created_by_me);
    return axios.get(`${API_URL}/api/knowledge-articles?${queryParams.toString()}`, { headers: getAuthHeader() });
  },
  getKnowledgeArticle: (id) => axios.get(`${API_URL}/api/knowledge-articles/${id}`, { headers: getAuthHeader() }),
  createKnowledgeArticle: (data) => axios.post(`${API_URL}/api/knowledge-articles`, data, { headers: getAuthHeader() }),
  updateKnowledgeArticle: (id, data) => axios.put(`${API_URL}/api/knowledge-articles/${id}`, data, { headers: getAuthHeader() }),
  deleteKnowledgeArticle: (id) => axios.delete(`${API_URL}/api/knowledge-articles/${id}`, { headers: getAuthHeader() }),
  submitArticleForReview: (id) => axios.post(`${API_URL}/api/knowledge-articles/${id}/submit-for-review`, {}, { headers: getAuthHeader() }),
  approveArticle: (id, comments, visibleToEndUsers) => axios.post(`${API_URL}/api/knowledge-articles/${id}/approve`, { review_comments: comments, visible_to_end_users: visibleToEndUsers }, { headers: getAuthHeader() }),
  rejectArticle: (id, comments) => axios.post(`${API_URL}/api/knowledge-articles/${id}/reject`, { review_comments: comments }, { headers: getAuthHeader() }),
  archiveArticle: (id) => axios.post(`${API_URL}/api/knowledge-articles/${id}/archive`, {}, { headers: getAuthHeader() }),
  articleFeedback: (id, helpful) => axios.post(`${API_URL}/api/knowledge-articles/${id}/feedback`, { helpful }, { headers: getAuthHeader() }),
  linkArticleToIncident: (articleId, incidentId) => axios.post(`${API_URL}/api/knowledge-articles/${articleId}/link-incident/${incidentId}`, {}, { headers: getAuthHeader() }),
  unlinkArticleFromIncident: (articleId, incidentId) => axios.delete(`${API_URL}/api/knowledge-articles/${articleId}/unlink-incident/${incidentId}`, { headers: getAuthHeader() }),

  // Survey
  getSurveyDetails: (token) => axios.get(`${API_URL}/api/survey/${token}`),
  submitSurvey: (data) => axios.post(`${API_URL}/api/survey/submit`, data),
  getSurveyAnalytics: () => axios.get(`${API_URL}/api/survey-analytics`, { headers: getAuthHeader() }),

  // NPS Surveys
  getNPSOptions: () => axios.get(`${API_URL}/api/nps/options`, { headers: getAuthHeader() }),
  getNPSSurveys: (status) => axios.get(`${API_URL}/api/nps/surveys${status ? `?status=${status}` : ''}`, { headers: getAuthHeader() }),
  getNPSSurvey: (id) => axios.get(`${API_URL}/api/nps/surveys/${id}`, { headers: getAuthHeader() }),
  createNPSSurvey: (data) => axios.post(`${API_URL}/api/nps/surveys`, data, { headers: getAuthHeader() }),
  updateNPSSurvey: (id, data) => axios.patch(`${API_URL}/api/nps/surveys/${id}`, data, { headers: getAuthHeader() }),
  deleteNPSSurvey: (id) => axios.delete(`${API_URL}/api/nps/surveys/${id}`, { headers: getAuthHeader() }),
  activateNPSSurvey: (id) => axios.post(`${API_URL}/api/nps/surveys/${id}/activate`, {}, { headers: getAuthHeader() }),
  sendNPSReminders: (id) => axios.post(`${API_URL}/api/nps/surveys/${id}/remind`, {}, { headers: getAuthHeader() }),
  getNPSSurveyForResponse: (token) => axios.get(`${API_URL}/api/nps/respond/${token}`),
  submitNPSResponse: (data) => axios.post(`${API_URL}/api/nps/respond`, data),

  // Comments and Work Notes
  addComment: (data) => axios.post(`${API_URL}/api/comments`, data, { headers: getAuthHeader() }),
  getComments: (ticketType, ticketId) => axios.get(`${API_URL}/api/comments/${ticketType}/${ticketId}`, { headers: getAuthHeader() }),

  // Assets
  getAssets: () => axios.get(`${API_URL}/api/assets`, { headers: getAuthHeader() }),
  getAsset: (id) => axios.get(`${API_URL}/api/assets/${id}`, { headers: getAuthHeader() }),
  createAsset: (data) => axios.post(`${API_URL}/api/assets`, data, { headers: getAuthHeader() }),
  updateAsset: (id, data) => axios.put(`${API_URL}/api/assets/${id}`, data, { headers: getAuthHeader() }),
  deleteAsset: (id) => axios.delete(`${API_URL}/api/assets/${id}`, { headers: getAuthHeader() }),
  downloadAssetTemplate: () => axios.get(`${API_URL}/api/assets/template/download`, { headers: getAuthHeader(), responseType: 'blob' }),
  bulkUploadAssets: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/api/assets/bulk-upload`, formData, { 
      headers: { 
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      } 
    });
  },

  // Users
  getUsers: () => axios.get(`${API_URL}/api/users`, { headers: getAuthHeader() }),
  getMyProfile: () => axios.get(`${API_URL}/api/users/me/profile`, { headers: getAuthHeader() }),
  updateMyProfile: (data) => axios.put(`${API_URL}/api/users/me/profile`, data, { headers: getAuthHeader() }),
  searchUsers: (query, limit = 10) => axios.get(`${API_URL}/api/users/search?q=${encodeURIComponent(query)}&limit=${limit}`, { headers: getAuthHeader() }),
  lookupUserByEmail: (email) => axios.get(`${API_URL}/api/users/lookup?email=${encodeURIComponent(email)}`, { headers: getAuthHeader() }),
  updateUser: (id, data) => axios.put(`${API_URL}/api/users/${id}`, data, { headers: getAuthHeader() }),
  updateUserRole: (id, role) => axios.patch(`${API_URL}/api/users/${id}/role`, { role }, { headers: getAuthHeader() }),
  createUser: (data) => axios.post(`${API_URL}/api/users/create`, data, { headers: getAuthHeader() }),
  deleteUser: (id) => axios.delete(`${API_URL}/api/users/${id}`, { headers: getAuthHeader() }),
  resetUserPassword: (id, newPassword) => axios.post(`${API_URL}/api/users/${id}/reset-password`, { new_password: newPassword }, { headers: getAuthHeader() }),
  downloadUserTemplate: () => axios.get(`${API_URL}/api/users/template/download`, { headers: getAuthHeader(), responseType: 'blob' }),
  bulkUploadUsers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/api/users/bulk-upload`, formData, { 
      headers: { 
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      } 
    });
  },

  // Tickets (ITIL Queue)
  getAllTickets: () => axios.get(`${API_URL}/api/tickets`, { headers: getAuthHeader() }),

  // Reports
  getSLAReport: (createdBy = null) => {
    const params = createdBy ? `?created_by=${encodeURIComponent(createdBy)}` : '';
    return axios.get(`${API_URL}/api/reports/sla${params}`, { headers: getAuthHeader() });
  },
  
  // Enhanced Reports - Incident Management
  getIncidentSummaryReport: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/api/reports/incidents/summary${query ? `?${query}` : ''}`, { headers: getAuthHeader() });
  },
  getIncidentSLADetailedReport: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/api/reports/incidents/sla-detailed${query ? `?${query}` : ''}`, { headers: getAuthHeader() });
  },
  getTechnicianPerformanceReport: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/api/reports/incidents/technician-performance${query ? `?${query}` : ''}`, { headers: getAuthHeader() });
  },
  getCategoryAnalysisReport: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/api/reports/incidents/category-analysis${query ? `?${query}` : ''}`, { headers: getAuthHeader() });
  },
  getIncidentAgingReport: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/api/reports/incidents/aging${query ? `?${query}` : ''}`, { headers: getAuthHeader() });
  },
  
  // Enhanced Reports - Executive Dashboard
  getExecutiveDashboard: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.assignment_group_id) queryParams.append('assignment_group_id', params.assignment_group_id);
    const queryString = queryParams.toString();
    return axios.get(`${API_URL}/api/reports/executive/dashboard${queryString ? '?' + queryString : ''}`, { headers: getAuthHeader() });
  },
  getExecutiveTrends: (days = 30) => axios.get(`${API_URL}/api/reports/executive/trends?days=${days}`, { headers: getAuthHeader() }),
  
  // Report Scheduling
  getReportSchedules: () => axios.get(`${API_URL}/api/reports/schedules`, { headers: getAuthHeader() }),
  createReportSchedule: (data) => axios.post(`${API_URL}/api/reports/schedules`, data, { headers: getAuthHeader() }),
  updateReportSchedule: (id, data) => axios.put(`${API_URL}/api/reports/schedules/${id}`, data, { headers: getAuthHeader() }),
  deleteReportSchedule: (id) => axios.delete(`${API_URL}/api/reports/schedules/${id}`, { headers: getAuthHeader() }),
  sendReportNow: (id) => axios.post(`${API_URL}/api/reports/schedules/${id}/send-now`, {}, { headers: getAuthHeader() }),
  
  // Report Export
  exportReport: (reportType, format = 'csv', params = {}) => {
    const query = new URLSearchParams({ format, ...params }).toString();
    return axios.get(`${API_URL}/api/reports/export/${reportType}?${query}`, { 
      headers: getAuthHeader(),
      responseType: format === 'csv' ? 'blob' : 'json'
    });
  },
  
  // Custom Reports
  getCustomReportModules: () => axios.get(`${API_URL}/api/reports/custom/modules`, { headers: getAuthHeader() }),
  getCustomReportFieldValues: (moduleId) => axios.get(`${API_URL}/api/reports/custom/field-values/${moduleId}`, { headers: getAuthHeader() }),
  previewCustomReport: (data) => axios.post(`${API_URL}/api/reports/custom/preview`, data, { headers: getAuthHeader() }),
  exportCustomReport: (data, format = 'csv') => {
    return axios.post(`${API_URL}/api/reports/custom/export?format=${format}`, data, { 
      headers: getAuthHeader(),
      responseType: format === 'csv' ? 'blob' : 'json'
    });
  },
  getSavedCustomReports: () => axios.get(`${API_URL}/api/reports/custom/saved`, { headers: getAuthHeader() }),
  saveCustomReport: (data) => axios.post(`${API_URL}/api/reports/custom/save`, data, { headers: getAuthHeader() }),
  updateSavedCustomReport: (id, data) => axios.put(`${API_URL}/api/reports/custom/saved/${id}`, data, { headers: getAuthHeader() }),
  deleteSavedCustomReport: (id) => axios.delete(`${API_URL}/api/reports/custom/saved/${id}`, { headers: getAuthHeader() }),
  scheduleCustomReport: (data) => axios.post(`${API_URL}/api/reports/custom/schedule`, data, { headers: getAuthHeader() }),

  // Assignment Groups
  getAssignmentGroups: () => axios.get(`${API_URL}/api/assignment-groups`, { headers: getAuthHeader() }),
  getAssignmentGroup: (id) => axios.get(`${API_URL}/api/assignment-groups/${id}`, { headers: getAuthHeader() }),
  createAssignmentGroup: (data) => axios.post(`${API_URL}/api/assignment-groups`, data, { headers: getAuthHeader() }),
  updateAssignmentGroup: (id, data) => axios.put(`${API_URL}/api/assignment-groups/${id}`, data, { headers: getAuthHeader() }),
  deleteAssignmentGroup: (id) => axios.delete(`${API_URL}/api/assignment-groups/${id}`, { headers: getAuthHeader() }),
  addGroupMember: (groupId, userId) => axios.post(`${API_URL}/api/assignment-groups/${groupId}/members`, { user_id: userId }, { headers: getAuthHeader() }),
  removeGroupMember: (groupId, userId) => axios.delete(`${API_URL}/api/assignment-groups/${groupId}/members/${userId}`, { headers: getAuthHeader() }),

  // SLA Configuration
  getSLAConfig: () => axios.get(`${API_URL}/api/sla-config`, { headers: getAuthHeader() }),
  createSLAConfig: (data) => axios.post(`${API_URL}/api/sla-config`, data, { headers: getAuthHeader() }),
  
  // New SLA Configuration APIs
  getDefaultSLAConfigs: () => axios.get(`${API_URL}/api/sla/default-configs`, { headers: getAuthHeader() }),
  getSLAConfigurations: () => axios.get(`${API_URL}/api/sla-configurations`, { headers: getAuthHeader() }),
  getSLAConfiguration: (id) => axios.get(`${API_URL}/api/sla-configurations/${id}`, { headers: getAuthHeader() }),
  createSLAConfiguration: (data) => axios.post(`${API_URL}/api/sla-configurations`, data, { headers: getAuthHeader() }),
  updateSLAConfiguration: (id, data) => axios.put(`${API_URL}/api/sla-configurations/${id}`, data, { headers: getAuthHeader() }),
  deleteSLAConfiguration: (id) => axios.delete(`${API_URL}/api/sla-configurations/${id}`, { headers: getAuthHeader() }),

  // Service Catalog
  getCatalogItems: (activeOnly = true) => axios.get(`${API_URL}/api/service-catalog?active_only=${activeOnly}`, { headers: getAuthHeader() }),
  getCatalogItem: (id) => axios.get(`${API_URL}/api/service-catalog/${id}`, { headers: getAuthHeader() }),
  createCatalogItem: (data) => axios.post(`${API_URL}/api/service-catalog`, data, { headers: getAuthHeader() }),
  updateCatalogItem: (id, data) => axios.put(`${API_URL}/api/service-catalog/${id}`, data, { headers: getAuthHeader() }),
  deleteCatalogItem: (id) => axios.delete(`${API_URL}/api/service-catalog/${id}`, { headers: getAuthHeader() }),

  // Task Flow Templates
  getTaskFlowTemplates: (catalogItemId = null) => axios.get(`${API_URL}/api/task-flow-templates${catalogItemId ? `?catalog_item_id=${catalogItemId}` : ''}`, { headers: getAuthHeader() }),
  getTaskFlowTemplate: (id) => axios.get(`${API_URL}/api/task-flow-templates/${id}`, { headers: getAuthHeader() }),
  createTaskFlowTemplate: (data) => axios.post(`${API_URL}/api/task-flow-templates`, data, { headers: getAuthHeader() }),
  updateTaskFlowTemplate: (id, data) => axios.put(`${API_URL}/api/task-flow-templates/${id}`, data, { headers: getAuthHeader() }),
  deleteTaskFlowTemplate: (id) => axios.delete(`${API_URL}/api/task-flow-templates/${id}`, { headers: getAuthHeader() }),

  // Predefined Tasks
  getPredefinedTasks: (category = null) => axios.get(`${API_URL}/api/predefined-tasks${category ? `?category=${category}` : ''}`, { headers: getAuthHeader() }),
  createPredefinedTask: (data) => axios.post(`${API_URL}/api/predefined-tasks`, data, { headers: getAuthHeader() }),
  updatePredefinedTask: (id, data) => axios.put(`${API_URL}/api/predefined-tasks/${id}`, data, { headers: getAuthHeader() }),
  deletePredefinedTask: (id) => axios.delete(`${API_URL}/api/predefined-tasks/${id}`, { headers: getAuthHeader() }),

  // Service Requests
  getServiceRequests: () => axios.get(`${API_URL}/api/service-requests`, { headers: getAuthHeader() }),
  getServiceRequest: (id) => axios.get(`${API_URL}/api/service-requests/${id}`, { headers: getAuthHeader() }),
  createServiceRequest: (data) => axios.post(`${API_URL}/api/service-requests`, data, { headers: getAuthHeader() }),
  resubmitServiceRequest: (id, data) => axios.put(`${API_URL}/api/service-requests/${id}/resubmit`, data, { headers: getAuthHeader() }),
  getLaptopProvisioningDashboard: () => axios.get(`${API_URL}/api/service-requests/dashboard/laptop-provisioning`, { headers: getAuthHeader() }),

  // Service Request Tasks
  createServiceRequestTask: (srId, data) => axios.post(`${API_URL}/api/service-requests/${srId}/tasks`, data, { headers: getAuthHeader() }),
  updateServiceRequestTask: (srId, taskId, data) => axios.put(`${API_URL}/api/service-requests/${srId}/tasks/${taskId}`, data, { headers: getAuthHeader() }),
  reorderServiceRequestTasks: (srId, taskOrder) => axios.post(`${API_URL}/api/service-requests/${srId}/tasks/reorder`, taskOrder, { headers: getAuthHeader() }),

  // Tasks
  getTasks: (params = {}) => {
    const urlParams = new URLSearchParams();
    if (params.sr_id) urlParams.append('sr_id', params.sr_id);
    if (params.assignment_group_id) urlParams.append('assignment_group_id', params.assignment_group_id);
    if (params.parent_type) urlParams.append('parent_type', params.parent_type);
    return axios.get(`${API_URL}/api/tasks?${urlParams.toString()}`, { headers: getAuthHeader() });
  },
  getTaskDetails: (taskId) => axios.get(`${API_URL}/api/tasks/${taskId}/details`, { headers: getAuthHeader() }),
  completeTask: (id, workNotes = null) => axios.put(`${API_URL}/api/tasks/${id}/complete`, { work_notes: workNotes }, { headers: getAuthHeader() }),
  acknowledgeTask: (id) => axios.put(`${API_URL}/api/tasks/${id}/acknowledge`, {}, { headers: getAuthHeader() }),
  assignTask: (id, userId) => axios.put(`${API_URL}/api/tasks/${id}/assign`, { assigned_to_id: userId }, { headers: getAuthHeader() }),
  startTask: (id) => axios.put(`${API_URL}/api/tasks/${id}/start`, {}, { headers: getAuthHeader() }),
  updateTask: (id, data) => axios.put(`${API_URL}/api/tasks/${id}/update`, data, { headers: getAuthHeader() }),
  
  // Task Attachments
  uploadTaskAttachment: (taskId, file, classification, comment = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('classification', classification);
    if (comment) formData.append('comment', comment);
    return axios.post(`${API_URL}/api/tasks/${taskId}/attachments?classification=${classification}&comment=${encodeURIComponent(comment)}`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteTaskAttachment: (taskId, attachmentId) => axios.delete(`${API_URL}/api/tasks/${taskId}/attachments/${attachmentId}`, { headers: getAuthHeader() }),
  getTaskAttachmentUrl: (taskId, attachmentId) => `${API_URL}/api/tasks/${taskId}/attachments/${attachmentId}`,
  
  // Task Activity Log
  addTaskActivityLog: (taskId, entry) => axios.post(`${API_URL}/api/tasks/${taskId}/activity-log`, entry, { headers: getAuthHeader() }),
  getTaskActivityLog: (taskId) => axios.get(`${API_URL}/api/tasks/${taskId}/activity-log`, { headers: getAuthHeader() }),
  
  // Approval Tasks
  approveTask: (id, decision, comments = '', isExpedited = false) => axios.put(`${API_URL}/api/tasks/${id}/approve`, { decision, comments, is_expedited: isExpedited }, { headers: getAuthHeader() }),
  getPendingApprovals: () => axios.get(`${API_URL}/api/tasks/approvals/pending`, { headers: getAuthHeader() }),

  // Email
  pollEmails: () => axios.post(`${API_URL}/api/email/process-manually`, {}, { headers: getAuthHeader() }),

  // Chatbot
  createChatConversation: (mode = 'traditional', language = 'en') => axios.post(`${API_URL}/api/chat/conversations`, { mode, language }, { headers: getAuthHeader() }),
  getUserConversations: () => axios.get(`${API_URL}/api/chat/conversations`, { headers: getAuthHeader() }),
  getConversation: (conversationId) => axios.get(`${API_URL}/api/chat/conversations/${conversationId}`, { headers: getAuthHeader() }),
  getConversationMessages: (conversationId) => axios.get(`${API_URL}/api/chat/conversations/${conversationId}/messages`, { headers: getAuthHeader() }),
  sendChatMessage: (conversationId, message) => axios.post(`${API_URL}/api/chat/conversations/${conversationId}/messages`, { message }, { headers: getAuthHeader() }),
  deleteConversation: (conversationId) => axios.delete(`${API_URL}/api/chat/conversations/${conversationId}`, { headers: getAuthHeader() }),
  sendIdleMessage: (conversationId) => axios.post(`${API_URL}/api/chat/conversations/${conversationId}/idle`, {}, { headers: getAuthHeader() }),
  // Chat history for agents
  getAllChatHistory: (userId = null, limit = 50) => {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    params.append('limit', limit);
    return axios.get(`${API_URL}/api/chat/conversations/history/all?${params}`, { headers: getAuthHeader() });
  },
  getConversationMessagesAgent: (conversationId) => axios.get(`${API_URL}/api/chat/conversations/${conversationId}/messages/agent-view`, { headers: getAuthHeader() }),
  transcribeVoice: (audioBlob, conversationId = null) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    if (conversationId) {
      formData.append('conversation_id', conversationId);
    }
    return axios.post(`${API_URL}/api/chat/voice/transcribe`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  textToSpeech: (text, voice = 'nova', speed = 1.0) => {
    return axios.post(`${API_URL}/api/chat/voice/speak`, { text, voice, speed }, { headers: getAuthHeader() });
  },

  // Workflow Automation
  getWorkflowVariables: (srId) => axios.get(`${API_URL}/api/workflow/variables/${srId}`, { headers: getAuthHeader() }),
  setTaskVariable: (taskId, data) => axios.put(`${API_URL}/api/workflow/tasks/${taskId}/set-variable`, data, { headers: getAuthHeader() }),
  getTaskAvailableVariables: (taskId) => axios.get(`${API_URL}/api/workflow/tasks/${taskId}/available-variables`, { headers: getAuthHeader() }),
  
  // Workflow Scheduling
  createSchedulingBooking: (data) => axios.post(`${API_URL}/api/workflow/scheduling/create-booking`, data, { headers: getAuthHeader() }),
  getBookingByToken: (token) => axios.get(`${API_URL}/api/workflow/scheduling/booking/${token}`),
  confirmBooking: (token, data) => axios.post(`${API_URL}/api/workflow/scheduling/booking/${token}/confirm`, data),
  getPendingBookings: () => axios.get(`${API_URL}/api/workflow/reminders/pending-bookings`, { headers: getAuthHeader() }),
  sendBookingReminder: (bookingId) => axios.post(`${API_URL}/api/workflow/reminders/send-booking-reminder/${bookingId}`, {}, { headers: getAuthHeader() }),
  
  // AI Workflow Assistance
  getAITaskSuggestions: (srId) => axios.get(`${API_URL}/api/workflow/ai/task-suggestions/${srId}`, { headers: getAuthHeader() }),
  executeAITask: (taskId) => axios.post(`${API_URL}/api/workflow/ai/execute-task/${taskId}`, {}, { headers: getAuthHeader() }),
  getAIAssetSuggestions: (srId, assetType = 'laptop', limit = 5) => axios.get(`${API_URL}/api/workflow/ai/suggest-assets/${srId}?asset_type=${assetType}&limit=${limit}`, { headers: getAuthHeader() }),
  selectAssetForSR: (srId, assetId, taskId) => axios.post(`${API_URL}/api/workflow/ai/select-asset/${srId}?asset_id=${assetId}&task_id=${taskId}`, {}, { headers: getAuthHeader() }),
  
  // Handover Confirmation
  sendHandoverConfirmation: (data) => axios.post(`${API_URL}/api/workflow/handover/send-confirmation`, data, { headers: getAuthHeader() }),
  
  // Amie Workflow Context
  getAmieWorkflowContext: (srId) => axios.get(`${API_URL}/api/workflow/amie/context/${srId}`, { headers: getAuthHeader() }),
  executeAmieAction: (srId, action, params = {}) => axios.post(`${API_URL}/api/workflow/amie/action/${srId}?action=${action}`, params, { headers: getAuthHeader() }),

  // Automated Reminder System
  runReminderScheduler: () => axios.post(`${API_URL}/api/reminders/run`, {}, { headers: getAuthHeader() }),
  runReminderSchedulerSync: () => axios.post(`${API_URL}/api/reminders/run-sync`, {}, { headers: getAuthHeader() }),
  getReminderStatus: () => axios.get(`${API_URL}/api/reminders/status`, { headers: getAuthHeader() }),
  getPendingReminders: () => axios.get(`${API_URL}/api/reminders/pending`, { headers: getAuthHeader() }),
  sendManualReminder: (reminderType, itemId) => axios.post(`${API_URL}/api/reminders/send-manual`, { reminder_type: reminderType, item_id: itemId }, { headers: getAuthHeader() }),
  getReminderLogs: (page = 1, limit = 20, runType = null) => axios.get(`${API_URL}/api/reminders/logs?page=${page}&limit=${limit}${runType ? `&run_type=${runType}` : ''}`, { headers: getAuthHeader() }),
  updateReminderConfig: (config) => axios.put(`${API_URL}/api/reminders/config`, config, { headers: getAuthHeader() }),

  // Module Subscriptions
  getModuleSubscriptions: () => axios.get(`${API_URL}/api/module-subscriptions`, { headers: getAuthHeader() }),
  updateModuleSubscriptions: (data) => axios.put(`${API_URL}/api/module-subscriptions`, data, { headers: getAuthHeader() }),
  
  // Enabled Modules (for sidebar filtering)
  getEnabledModules: () => axios.get(`${API_URL}/api/enabled-modules`, { headers: getAuthHeader() }),

  // Integration Configurations
  // Directory Integration
  getDirectoryConfig: () => axios.get(`${API_URL}/api/integrations/directory`, { headers: getAuthHeader() }),
  saveDirectoryConfig: (config) => axios.post(`${API_URL}/api/integrations/directory`, config, { headers: getAuthHeader() }),
  testDirectoryConnection: (config) => axios.post(`${API_URL}/api/integrations/directory/test`, config, { headers: getAuthHeader() }),
  syncDirectoryUsers: () => axios.post(`${API_URL}/api/integrations/directory/sync`, {}, { headers: getAuthHeader() }),
  getDirectorySyncHistory: (limit = 20) => axios.get(`${API_URL}/api/integrations/directory/sync-history?limit=${limit}`, { headers: getAuthHeader() }),

  // Google OAuth
  getGoogleAuthUrl: () => axios.get(`${API_URL}/api/integrations/google/auth-url`, { headers: getAuthHeader() }),
  disconnectGoogle: () => axios.post(`${API_URL}/api/integrations/google/disconnect`, {}, { headers: getAuthHeader() }),
  syncGoogleUsers: () => axios.post(`${API_URL}/api/integrations/google/sync`, {}, { headers: getAuthHeader() }),

  // Email Integration
  getEmailConfig: () => axios.get(`${API_URL}/api/integrations/email`, { headers: getAuthHeader() }),
  saveEmailConfig: (config) => axios.post(`${API_URL}/api/integrations/email`, config, { headers: getAuthHeader() }),
  testEmailConnection: () => axios.post(`${API_URL}/api/integrations/email/test`, {}, { headers: getAuthHeader() }),
  // Call Center (Twilio) integration
  getCallCenterConfig: () => axios.get(`${API_URL}/api/integrations/call-center`, { headers: getAuthHeader() }),
  saveCallCenterConfig: (config) => axios.post(`${API_URL}/api/integrations/call-center`, config, { headers: getAuthHeader() }),
  testCallCenterConnection: () => axios.post(`${API_URL}/api/integrations/call-center/test`, {}, { headers: getAuthHeader() }),
  sendTestEmail: () => axios.post(`${API_URL}/api/integrations/email/send-test`, {}, { headers: getAuthHeader() }),

  // Categories (Incidents)
  getCategories: () => axios.get(`${API_URL}/api/categories`, { headers: getAuthHeader() }),
  createCategory: (data) => axios.post(`${API_URL}/api/categories`, data, { headers: getAuthHeader() }),
  updateCategory: (id, data) => axios.put(`${API_URL}/api/categories/${id}`, data, { headers: getAuthHeader() }),
  deleteCategory: (id) => axios.delete(`${API_URL}/api/categories/${id}`, { headers: getAuthHeader() }),
  addSubCategory: (categoryId, data) => axios.post(`${API_URL}/api/categories/${categoryId}/subcategories`, data, { headers: getAuthHeader() }),
  updateSubCategory: (categoryId, subCategoryId, data) => axios.put(`${API_URL}/api/categories/${categoryId}/subcategories/${subCategoryId}`, data, { headers: getAuthHeader() }),
  deleteSubCategory: (categoryId, subCategoryId) => axios.delete(`${API_URL}/api/categories/${categoryId}/subcategories/${subCategoryId}`, { headers: getAuthHeader() }),

  // Problem Categories
  getProblemCategories: () => axios.get(`${API_URL}/api/problem-categories`, { headers: getAuthHeader() }),
  createProblemCategory: (data) => axios.post(`${API_URL}/api/problem-categories`, data, { headers: getAuthHeader() }),
  updateProblemCategory: (id, data) => axios.put(`${API_URL}/api/problem-categories/${id}`, data, { headers: getAuthHeader() }),
  deleteProblemCategory: (id) => axios.delete(`${API_URL}/api/problem-categories/${id}`, { headers: getAuthHeader() }),
  addProblemSubCategory: (categoryId, data) => axios.post(`${API_URL}/api/problem-categories/${categoryId}/subcategories`, data, { headers: getAuthHeader() }),
  updateProblemSubCategory: (categoryId, subCategoryId, data) => axios.put(`${API_URL}/api/problem-categories/${categoryId}/subcategories/${subCategoryId}`, data, { headers: getAuthHeader() }),
  deleteProblemSubCategory: (categoryId, subCategoryId) => axios.delete(`${API_URL}/api/problem-categories/${categoryId}/subcategories/${subCategoryId}`, { headers: getAuthHeader() }),

  // Email Reply
  sendEmailReply: (incidentId, data) => axios.post(`${API_URL}/api/incidents/${incidentId}/reply-email`, data, { headers: getAuthHeader() }),

  // Bulk Delete (Super Admin only)
  bulkDeleteIncidents: (ids, reason) => axios.post(`${API_URL}/api/incidents/bulk-delete`, { ids, reason }, { headers: getAuthHeader() }),
  bulkActionIncidents: (data) => axios.post(`${API_URL}/api/incidents/bulk-action`, data, { headers: getAuthHeader() }),
  bulkDeleteRequests: (ids, reason) => axios.post(`${API_URL}/api/requests/bulk-delete`, { ids, reason }, { headers: getAuthHeader() }),
  bulkDeleteChanges: (ids, reason) => axios.post(`${API_URL}/api/changes/bulk-delete`, { ids, reason }, { headers: getAuthHeader() }),
  bulkDeleteProblems: (ids, reason) => axios.post(`${API_URL}/api/problems/bulk-delete`, { ids, reason }, { headers: getAuthHeader() }),
  bulkDeleteServiceRequests: (ids, reason) => axios.post(`${API_URL}/api/service-requests/bulk-delete`, { ids, reason }, { headers: getAuthHeader() }),

  // Deleted Items Management (Super Admin only)
  getDeletedItems: () => axios.get(`${API_URL}/api/deleted-items`, { headers: getAuthHeader() }),
  restoreDeletedItem: (type, id) => axios.post(`${API_URL}/api/deleted-items/${type}/${id}/restore`, {}, { headers: getAuthHeader() }),
  permanentlyDeleteItem: (type, id) => axios.delete(`${API_URL}/api/deleted-items/${type}/${id}/permanent`, { headers: getAuthHeader() }),
  bulkRestoreItems: (items) => axios.post(`${API_URL}/api/deleted-items/bulk-restore`, { items }, { headers: getAuthHeader() }),

  // Audit Logs (Super Admin only)
  createAuditLog: (data) => axios.post(`${API_URL}/api/audit-logs`, data, { headers: getAuthHeader() }),
  getAuditLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.action) queryParams.append('action', params.action);
    if (params.actor_id) queryParams.append('actor_id', params.actor_id);
    if (params.target_user_id) queryParams.append('target_user_id', params.target_user_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    return axios.get(`${API_URL}/api/audit-logs?${queryParams.toString()}`, { headers: getAuthHeader() });
  },
  getAuditLogStats: () => axios.get(`${API_URL}/api/audit-logs/stats`, { headers: getAuthHeader() }),

  // Generic methods for flexibility
  get: (path) => axios.get(`${API_URL}/api${path}`, { headers: getAuthHeader() }),
  post: (path, data) => axios.post(`${API_URL}/api${path}`, data, { headers: getAuthHeader() }),
  put: (path, data) => axios.put(`${API_URL}/api${path}`, data, { headers: getAuthHeader() }),
  delete: (path) => axios.delete(`${API_URL}/api${path}`, { headers: getAuthHeader() }),

  // Notifications
  getNotifications: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.unread_only) queryParams.append('unread_only', 'true');
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    return axios.get(`${API_URL}/api/notifications?${queryParams.toString()}`, { headers: getAuthHeader() }).then(r => r.data);
  },
  getUnreadNotificationCount: () => axios.get(`${API_URL}/api/notifications/unread-count`, { headers: getAuthHeader() }).then(r => r.data),
  markNotificationRead: (id) => axios.post(`${API_URL}/api/notifications/${id}/read`, {}, { headers: getAuthHeader() }),
  markAllNotificationsRead: () => axios.post(`${API_URL}/api/notifications/mark-all-read`, {}, { headers: getAuthHeader() }),
  deleteNotification: (id) => axios.delete(`${API_URL}/api/notifications/${id}`, { headers: getAuthHeader() }),
  clearAllNotifications: () => axios.delete(`${API_URL}/api/notifications`, { headers: getAuthHeader() }),
  
  // Notification Preferences
  getNotificationPreferences: () => axios.get(`${API_URL}/api/notifications/preferences`, { headers: getAuthHeader() }).then(r => r.data),
  updateNotificationPreferences: (data) => axios.put(`${API_URL}/api/notifications/preferences`, data, { headers: getAuthHeader() }).then(r => r.data),
  resetNotificationPreferences: () => axios.post(`${API_URL}/api/notifications/preferences/reset`, {}, { headers: getAuthHeader() }).then(r => r.data),

  // Announcements (Scrolling Banners)
  getAnnouncements: (activeOnly = true) => axios.get(`${API_URL}/api/announcements?active_only=${activeOnly}`, { headers: getAuthHeader() }).then(r => r.data),
  getActiveAnnouncements: () => axios.get(`${API_URL}/api/announcements/active`, { headers: getAuthHeader() }).then(r => r.data),
  getForcedPopupAnnouncements: () => axios.get(`${API_URL}/api/announcements/forced-popups`, { headers: getAuthHeader() }).then(r => r.data),
  acknowledgeAnnouncement: (id, source = 'app') => axios.post(`${API_URL}/api/announcements/${id}/acknowledge?source=${source}`, {}, { headers: getAuthHeader() }).then(r => r.data),
  acknowledgeAnnouncementViaEmail: (id, userId, token) => axios.post(`${API_URL}/api/announcements/${id}/acknowledge-email?user_id=${userId}&token=${token}`).then(r => r.data),
  getAcknowledgmentReport: (id) => axios.get(`${API_URL}/api/announcements/${id}/acknowledgment-report`, { headers: getAuthHeader() }).then(r => r.data),
  downloadAcknowledgmentReport: (id) => axios.get(`${API_URL}/api/announcements/${id}/acknowledgment-report/download`, { headers: getAuthHeader(), responseType: 'blob' }).then(r => r),
  createAnnouncement: (data) => axios.post(`${API_URL}/api/announcements`, data, { headers: getAuthHeader() }).then(r => r.data),
  updateAnnouncement: (id, data) => axios.put(`${API_URL}/api/announcements/${id}`, data, { headers: getAuthHeader() }).then(r => r.data),
  deleteAnnouncement: (id) => axios.delete(`${API_URL}/api/announcements/${id}`, { headers: getAuthHeader() }),
  deactivateAnnouncement: (id) => axios.post(`${API_URL}/api/announcements/${id}/deactivate`, {}, { headers: getAuthHeader() }),
  activateAnnouncement: (id) => axios.post(`${API_URL}/api/announcements/${id}/activate`, {}, { headers: getAuthHeader() }),
  getSupportGroupsForAnnouncement: () => axios.get(`${API_URL}/api/announcements/support-groups`, { headers: getAuthHeader() }).then(r => r.data),
  searchIncidentsForAnnouncement: (query = '') => axios.get(`${API_URL}/api/announcements/incidents/search?query=${encodeURIComponent(query)}`, { headers: getAuthHeader() }).then(r => r.data),

  // ==================== LIVE CHAT ====================
  // Agent Status
  getAgentStatus: () => axios.get(`${API_URL}/api/live-chat/agent/status`, { headers: getAuthHeader() }).then(r => r.data),
  updateAgentStatus: (data) => axios.put(`${API_URL}/api/live-chat/agent/status`, data, { headers: getAuthHeader() }).then(r => r.data),
  agentHeartbeat: () => axios.post(`${API_URL}/api/live-chat/agent/heartbeat`, {}, { headers: getAuthHeader() }).then(r => r.data),
  getOnlineAgents: () => axios.get(`${API_URL}/api/live-chat/agents/online`, { headers: getAuthHeader() }).then(r => r.data),
  
  // Chat Sessions
  createChatSession: (data) => axios.post(`${API_URL}/api/live-chat/sessions`, data, { headers: getAuthHeader() }).then(r => r.data),
  getChatSessions: (status = null) => axios.get(`${API_URL}/api/live-chat/sessions${status ? `?status=${status}` : ''}`, { headers: getAuthHeader() }).then(r => r.data),
  getChatSession: (sessionId) => axios.get(`${API_URL}/api/live-chat/sessions/${sessionId}`, { headers: getAuthHeader() }).then(r => r.data),
  pickupChat: (sessionId) => axios.post(`${API_URL}/api/live-chat/sessions/${sessionId}/pickup`, {}, { headers: getAuthHeader() }).then(r => r.data),
  closeChat: (sessionId, resolutionSummary = null) => axios.post(`${API_URL}/api/live-chat/sessions/${sessionId}/close`, null, { headers: getAuthHeader(), params: { resolution_summary: resolutionSummary } }).then(r => r.data),
  sendLiveChatMessage: (sessionId, data) => axios.post(`${API_URL}/api/live-chat/sessions/${sessionId}/messages`, data, { headers: getAuthHeader() }).then(r => r.data),
  transferChat: (sessionId, targetAgentId, reason = null, notes = null) => axios.post(`${API_URL}/api/live-chat/sessions/${sessionId}/transfer`, { session_id: sessionId, target_agent_id: targetAgentId, reason, notes }, { headers: getAuthHeader() }).then(r => r.data),
  
  // Transfer helpers
  getAvailableAgents: () => axios.get(`${API_URL}/api/live-chat/agents/available`, { headers: getAuthHeader() }).then(r => r.data),
  getTransferReasons: () => axios.get(`${API_URL}/api/live-chat/transfer/reasons`, { headers: getAuthHeader() }).then(r => r.data),
  
  // Amie Learning
  extractQAPairs: (sessionId) => axios.post(`${API_URL}/api/live-chat/sessions/${sessionId}/extract-qa`, {}, { headers: getAuthHeader() }).then(r => r.data),
  saveQAPairs: (sessionId, qaPairs) => axios.post(`${API_URL}/api/live-chat/sessions/${sessionId}/save-qa`, qaPairs, { headers: getAuthHeader() }).then(r => r.data),
  getLearnedQA: (status = null, limit = 50, skip = 0) => axios.get(`${API_URL}/api/live-chat/learned-qa`, { headers: getAuthHeader(), params: { status, limit, skip } }).then(r => r.data),
  updateLearnedQA: (qaId, updates) => axios.put(`${API_URL}/api/live-chat/learned-qa/${qaId}`, updates, { headers: getAuthHeader() }).then(r => r.data),
  deleteLearnedQA: (qaId) => axios.delete(`${API_URL}/api/live-chat/learned-qa/${qaId}`, { headers: getAuthHeader() }).then(r => r.data),
  getLearnedQAStats: () => axios.get(`${API_URL}/api/live-chat/learned-qa/stats`, { headers: getAuthHeader() }).then(r => r.data),
  
  // Transcript
  getChatTranscript: (sessionId, format = 'json') => axios.get(`${API_URL}/api/live-chat/sessions/${sessionId}/transcript`, { headers: getAuthHeader(), params: { format } }).then(r => r.data),
  
  // Create Resolved Ticket on Close
  createResolvedTicket: (sessionId, title, description = '', assignmentGroupId = '') => axios.post(`${API_URL}/api/live-chat/sessions/${sessionId}/create-resolved-ticket`, null, { headers: getAuthHeader(), params: { title, description, assignment_group_id: assignmentGroupId } }).then(r => r.data),
  
  // Assignment Groups & Agent Availability (for ITIL staff)
  getGroupsWithAgents: () => axios.get(`${API_URL}/api/live-chat/groups/with-agents`, { headers: getAuthHeader() }).then(r => r.data),
  getAgentsByGroup: (groupId) => axios.get(`${API_URL}/api/live-chat/groups/${groupId}/agents`, { headers: getAuthHeader() }).then(r => r.data),
  checkAgentAvailability: () => axios.get(`${API_URL}/api/live-chat/availability/check`, { headers: getAuthHeader() }).then(r => r.data),
  createSessionOrTicket: (data) => axios.post(`${API_URL}/api/live-chat/sessions/create-or-ticket`, data, { headers: getAuthHeader() }).then(r => r.data),
  
  // Queue
  getChatQueue: () => axios.get(`${API_URL}/api/live-chat/queue`, { headers: getAuthHeader() }).then(r => r.data),
  getChatQueueStats: () => axios.get(`${API_URL}/api/live-chat/queue/stats`, { headers: getAuthHeader() }).then(r => r.data),

  // Auto-Routing Rules
  matchAutoRoutingRule: (data) => axios.post(`${API_URL}/api/auto-routing-rules/match`, data, { headers: getAuthHeader() }),
  getAutoRoutingRules: () => axios.get(`${API_URL}/api/auto-routing-rules`, { headers: getAuthHeader() }),
  createAutoRoutingRule: (data) => axios.post(`${API_URL}/api/auto-routing-rules`, data, { headers: getAuthHeader() }),
  updateAutoRoutingRule: (id, data) => axios.put(`${API_URL}/api/auto-routing-rules/${id}`, data, { headers: getAuthHeader() }),
  deleteAutoRoutingRule: (id) => axios.delete(`${API_URL}/api/auto-routing-rules/${id}`, { headers: getAuthHeader() }),

  // WebSocket URL for live chat
  getLiveChatWebSocketUrl: (userId) => `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/live-chat/ws/${userId}`
};